
import React, { useMemo } from 'react';
import RenderIf from 'components/RenderIf/RenderIf';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { isEmpty } from 'lodash';
import { getSaleIndexForCadence } from 'views/ProFormaTable/helpers';
import TopLevelAnalysis from './TopLevelAnalysis/TopLevelAnalysis';
import ProFormaGenericTable from '../../ProFormaGenericTable/ProFormaGenericTable';
import {
    WATERFALL_SUMMARY_COLUMNS,
    transformWaterfallSummaryRows,
    transformGpContributionsRows,
    transformLpContributionsRows,
    transformDistributionWaterfallRows,
    transformConsolidatedDistributionRows,
    transformConsolidatedNetCashFlowRows,
} from './constants';
import './WaterfallAnalysis.scss';
import ContributionsAndDistributions from './ContributionsAndDistributions/ContributionsAndDistributions';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import { getContributionsTotals } from './ContributionsAndDistributions/constants';

export interface IWaterfallAnalysisProps {
    hasWriteAccessToProForma: boolean;
}

const WaterfallAnalysis = ({ hasWriteAccessToProForma }: IWaterfallAnalysisProps) => {
    const {
        waterfallAnalysis,
        handleUpdateDistributionWaterfall,
        handleUpdateProFormaPartners,
        proForma,
        cashFlowPeriodsByCadence,
        periodCadence,
        operatingInfo,
    } = useProFormaSocketContext();

    const saleIndex = getSaleIndexForCadence(operatingInfo.saleMonth, periodCadence);

    const includeLp = waterfallAnalysis?.includeLp ?? true;

    const gpContributionsRowsPopulated = React.useMemo(
        () => transformGpContributionsRows(
            waterfallAnalysis?.gp,
            waterfallAnalysis?.gpGpContributions,
            waterfallAnalysis?.gpGpContributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ),
        [
            waterfallAnalysis?.gp,
            waterfallAnalysis?.gpGpContributions,
            waterfallAnalysis?.gpGpContributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ],
    );

    const lpContributionsRowsPopulated = React.useMemo(
        () => transformLpContributionsRows(
            waterfallAnalysis?.lp,
            waterfallAnalysis?.contributions,
            waterfallAnalysis?.contributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ),
        [
            waterfallAnalysis?.lp,
            waterfallAnalysis?.contributions,
            waterfallAnalysis?.contributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ],
    );

    const distributionWaterfallRowsPopulated = React.useMemo(
        () => transformDistributionWaterfallRows(
            waterfallAnalysis?.distributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionWaterfallCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.distributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionWaterfallCashFlow,
            saleIndex,
        ],
    );

    const distributionsRowsPopulated = React.useMemo(
        () => transformConsolidatedDistributionRows(
            waterfallAnalysis?.gpGpDistributions,
            waterfallAnalysis?.distributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionsCashFlow,
            waterfallAnalysis?.distributionsCashFlow,
            saleIndex,
            includeLp,
        ),
        [
            waterfallAnalysis?.gpGpDistributions,
            waterfallAnalysis?.distributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionsCashFlow,
            waterfallAnalysis?.distributionsCashFlow,
            saleIndex,
            includeLp,
        ],
    );

    const netCashFlowRowsPopulated = React.useMemo(
        () => transformConsolidatedNetCashFlowRows(
            waterfallAnalysis?.gpGpNetCashFlow,
            waterfallAnalysis?.netCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpNetCashFlowCashFlow,
            waterfallAnalysis?.netCashFlowCashFlow,
            saleIndex,
            includeLp,
        ),
        [
            waterfallAnalysis?.gpGpNetCashFlow,
            waterfallAnalysis?.netCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpNetCashFlowCashFlow,
            waterfallAnalysis?.netCashFlowCashFlow,
            saleIndex,
            includeLp,
        ],
    );

    const waterfallSummaryRows = React.useMemo(
        () => transformWaterfallSummaryRows(waterfallAnalysis?.waterfallSummary, includeLp),
        [waterfallAnalysis?.waterfallSummary, includeLp],
    );

    const updateDistributionWaterfallRow = (rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        const updateMap: Record<number, Record<string, Record<string, number>>> = {
            0: {
                upTo: { preferredReturnHurdleRate: value },
            },
            1: {
                upTo: { secondTierHurdleRate: value },
                gpPromote: { secondTierGpPromote: value },
            },
            2: {
                upTo: { thirdTierHurdleRate: value },
                gpPromote: { thirdTierGpPromote: value },
            },
            3: {
                gpPromote: { fourthTierGpPromote: value },
            },
        };

        const updateData = updateMap[rowId]?.[dataKey];
        if (updateData) {
            handleUpdateDistributionWaterfall('basic', updateData);
        }
    };

    const updateContributionRow = (rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = dataKey === 'share'
            ? (parseFloat(e.target.value)).toString()
            : e.target.value;

        const updateData = {
            id: rowId,
            [dataKey === 'share' ? 'contributionShare' : 'name']: value,
        };

        handleUpdateProFormaPartners(updateData);
    };

    const gpContributionsTotalsWithPeriods = useMemo(() => {
        const totals = getContributionsTotals(
            waterfallAnalysis.gpGpContributionsCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        );
        return {
            amount: waterfallAnalysis?.gpGpContributions?.totalEquityAmount,
            share: waterfallAnalysis?.gpGpContributions?.totalShareOfDevFee,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.gpGpContributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, saleIndex]);

    const lpContributionsTotalsWithPeriods = useMemo(() => {
        const totals: Record<string, string | undefined> = {};
        const allPeriods = periodCadence && cashFlowPeriodsByCadence ? cashFlowPeriodsByCadence[periodCadence] : [];
        const periods = saleIndex !== undefined ? allPeriods.slice(0, saleIndex + 1) : allPeriods;

        periods.forEach((_, index) => {
            totals[`column${index + 1}`] = waterfallAnalysis?.contributionsCashFlow?.[index]?.lpAmount;
        });

        return {
            amount: waterfallAnalysis?.contributions?.lpAmount,
            share: waterfallAnalysis?.contributions?.lpSharePercent,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.contributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, saleIndex]);

    const distributionTotalsWithPeriods = useMemo(() => {
        const totals = getContributionsTotals(
            waterfallAnalysis.distributionsCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        );
        return {
            amount: waterfallAnalysis?.distributions?.totalAmount,
            share: waterfallAnalysis?.distributions?.totalShare,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.distributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, saleIndex]);

    const netCashFlowTotalsWithPeriods = useMemo(() => {
        const totals = getContributionsTotals(
            waterfallAnalysis.netCashFlowCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        );
        return {
            amount: waterfallAnalysis?.netCashFlow?.totalAmount,
            share: waterfallAnalysis?.netCashFlow?.totalShare,
            emx: waterfallAnalysis?.netCashFlow?.totalEmx,
            irr: waterfallAnalysis?.netCashFlow?.totalIrr,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.netCashFlow, cashFlowPeriodsByCadence, periodCadence, saleIndex]);

    return (
        <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
            <RenderIf isTrue={!isEmpty(proForma)} isLoading={isEmpty(proForma)}>
                <div className="WaterfallAnalysis">
                    <TopLevelAnalysis />
                    <div className="WaterfallAnalysis__separator mx-3 mb-3" />
                    <div className="w-100 px-3 pb-3">
                        <ProFormaGenericTable
                            fixedColumnsMaxWidth="100%"
                            columns={WATERFALL_SUMMARY_COLUMNS}
                            rows={waterfallSummaryRows}
                            setRows={() => { }}
                            hideAddButton
                            hideTotalFooter
                            hideCashFlowTotals
                        />
                    </div>
                    <div className="WaterfallAnalysis__separator mx-3 mb-3" />
                    <div className="w-100 px-3 pb-3">
                        <ContributionsAndDistributions
                            gpContributionsRows={gpContributionsRowsPopulated}
                            lpContributionsRows={lpContributionsRowsPopulated}
                            distributionWaterfallRows={distributionWaterfallRowsPopulated}
                            updateDistributionWaterfallRow={updateDistributionWaterfallRow}
                            updateContributionRow={updateContributionRow}
                            distributionsRows={distributionsRowsPopulated}
                            netCashFlowRows={netCashFlowRowsPopulated}
                            gpContributionsTotals={gpContributionsTotalsWithPeriods}
                            lpContributionsTotals={lpContributionsTotalsWithPeriods}
                            distributionsTotals={distributionTotalsWithPeriods}
                            netCashFlowTotals={netCashFlowTotalsWithPeriods}
                        />
                    </div>
                </div>
            </RenderIf>
        </ReadOnlyWrapper>
    );
};

export default WaterfallAnalysis;
